import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Box, Col, getCSSVariable, Link, Text } from '@opendoor/bricks-next';
import { motion } from 'framer-motion';

import type { MegaNavItem } from '../../types';
import { GLASS_HEADER_HEIGHT } from './constants';
import styles from './GlassHeader.module.css';

interface GlassMegaNavFlyoutProps {
  item: MegaNavItem;
  isVisible: boolean;
}

const flyoutVariants = {
  hidden: {
    opacity: 0,
    y: -8,
    transition: { duration: 0.2, ease: 'easeOut' },
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.2, ease: 'easeOut' },
  },
};

const GlassMegaNavFlyout: React.FC<GlassMegaNavFlyoutProps> = ({ item, isVisible }) => {
  const [isAnimationComplete, setIsAnimationComplete] = useState(false);
  const flyoutRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isVisible) {
      setIsAnimationComplete(false);
    }
  }, [isVisible]);

  const handleAnimationComplete = useCallback((definition: string) => {
    if (definition === 'visible') {
      setIsAnimationComplete(true);
      const firstLink = flyoutRef.current?.querySelector('a');
      firstLink?.focus();
    }
  }, []);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    const links = flyoutRef.current?.querySelectorAll('a');
    if (!links?.length) {
      return;
    }

    const currentIndex = Array.from(links).indexOf(document.activeElement as HTMLAnchorElement);
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const next = currentIndex < links.length - 1 ? currentIndex + 1 : 0;
      links[next].focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const prev = currentIndex > 0 ? currentIndex - 1 : links.length - 1;
      links[prev].focus();
    }
  }, []);

  return (
    <motion.div
      ref={flyoutRef}
      initial="hidden"
      animate={isVisible ? 'visible' : 'hidden'}
      variants={flyoutVariants}
      onAnimationComplete={handleAnimationComplete}
      onKeyDown={handleKeyDown}
      role="menu"
      aria-label={`${item.name} submenu`}
      style={{
        position: 'fixed',
        top: `${GLASS_HEADER_HEIGHT}px`,
        left: 0,
        right: 0,
        borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
        zIndex: 1000,
        visibility: isVisible ? 'visible' : 'hidden',
      }}
    >
      <Box
        width="100%"
        backgroundColor="$backgroundInversePureBlack"
        pointerEvents={isVisible && isAnimationComplete ? 'auto' : 'none'}
      >
        <Box
          maxWidth="$328"
          px="$5"
          pt="$10"
          pb="$11"
          mx="auto"
          gap="$12"
          $platform-web={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr 1fr',
          }}
        >
          {item.sections?.map((section, sectionIndex) => (
            <Col key={sectionIndex}>
              <Box tag="p" display="block" mb="$1half" $platform-web={{ letterSpacing: '-0.22px' }}>
                <Text tag="span" typography="$labelMedium" color="$contentStateInactiveAccentBrand">
                  {section.title}
                </Text>
              </Box>
              <Col gap="$2" mt="$4">
                {section.links.map((link, linkIndex) => (
                  <Link
                    key={linkIndex}
                    href={link.href}
                    textDecorationLine="none"
                    display="block"
                    role="menuitem"
                    aria-label={`Navigate to ${link.name}`}
                    analyticsName={`cosmos-glass-nav-${item.name.toLowerCase()}-${link.name
                      .toLowerCase()
                      .replace(/\s+/g, '-')}`}
                    className={styles.flyoutLink}
                    style={
                      {
                        '--glass-flyout-hover-color': getCSSVariable(
                          '$contentInversePrimary',
                          'color',
                        ),
                      } as React.CSSProperties
                    }
                    $platform-web={{ transition: 'color 0.15s' }}
                  >
                    <Text
                      tag="span"
                      typography="$bodyMedium"
                      color="white"
                      opacity={0.75}
                      $platform-web={{ transition: 'opacity 0.15s' }}
                      hoverStyle={{ opacity: 1 }}
                    >
                      {link.name}
                    </Text>
                  </Link>
                ))}
              </Col>
            </Col>
          ))}
        </Box>
      </Box>
    </motion.div>
  );
};

export default GlassMegaNavFlyout;
